// EventNode.jsx - Restored original styling with injects and colors
import React from 'react';
import { Handle, Position } from 'reactflow';

export default function EventNode({ data, selected }) {
  const { block, activeBlockId } = data;
  const isActive = block.id === activeBlockId;
  
  // Calculate dynamic height based on content
  const injectsHeight = block.injects ? block.injects.length * 35 : 0;
  const autoProceedHeight = block.autoProceed ? 20 : 0;
  const height = 120 + injectsHeight + autoProceedHeight;

  // Format duration for display
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs}s`;
    if (secs === 0) return `${mins}m`;
    return `${mins}m ${secs}s`;
  };

  return (
    <div 
      className={`
        w-[280px] rounded-lg bg-white border-2 shadow-md transition-all relative overflow-hidden
        ${isActive ? 'border-blue-600 shadow-lg' : 'border-gray-200'}
        ${selected ? 'ring-2 ring-blue-500' : ''}
        hover:shadow-lg
      `}
      style={{ height: `${height}px` }}
    >
      {/* Node header */}
      <div className={`flex items-center justify-between p-2 border-b rounded-t-lg ${
        isActive ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
      }`}>
        <h3 className="font-semibold text-gray-800 text-sm">
          {block.title || 'Event Block'}
        </h3>
        <div className="flex items-center gap-2">
          {block.duration && (
            <span className="text-xs text-gray-500">
              {formatDuration(block.duration)}
            </span>
          )}
          {block.autoProceed && (
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
              Auto
            </span>
          )}
        </div>
      </div>
      
      {/* Node content */}
      <div className="p-3">
        {block.description && (
          <p className="text-xs text-gray-600 mb-2">
            {block.description}
          </p>
        )}

        {/* Injects */}
        {block.injects?.map((inject, idx) => (
          <div 
            key={idx} 
            className="mb-1 p-1 bg-yellow-50 border border-yellow-200 rounded text-xs"
          >
            <div className="flex justify-between text-amber-800">
              <span className="font-medium">{inject.type}</span>
              {inject.time && <span>{inject.time}s</span>}
            </div>
            <div className="text-amber-700 mt-0.5">
              {inject.content.slice(0, 35)}...
            </div>
          </div>
        ))}

        {/* Auto-proceed info */}
        {block.autoProceed && (
          <div className="mt-2 text-xs text-blue-700 bg-blue-50 px-2 py-1 rounded">
            Continues automatically
          </div>
        )}
      </div>

      {/* Event indicator dot */}
      <div className="absolute top-2 right-2 w-3 h-3 bg-blue-500 rounded-full" />

      {/* Connection handles */}
      <Handle 
        type="target" 
        position={Position.Left} 
        className="w-3 h-3 !bg-blue-500"
      />
      {(block.next || block.autoProceed) && (
        <Handle 
          type="source" 
          position={Position.Right} 
          className="w-3 h-3 !bg-blue-500"
        />
      )}
    </div>
  );
}